/**
 * Clerk session signals shared by the server (route handlers, server actions)
 * and the browser. Pure functions, no Clerk SDK import, so Clerk-free surfaces
 * (marketing, ads, token pages) can use them without shipping Clerk.
 *
 * Why this exists. Marketing and token pages ship no Clerk for anonymous
 * visitors (components/auth/clerk-scope.tsx). A signed-in visitor's 60-second
 * session JWT (`__session`) can therefore be stale when they act there, before
 * the signed-in upkeep (components/auth/signed-in-clerk-upkeep.tsx) has loaded
 * clerk-js and refreshed it. clerkMiddleware then authenticates the request as
 * signed OUT, but stamps the reason on the forwarded request. A stale signed-in
 * visitor must be told apart from a genuinely anonymous one, so a route never
 * downgrades them to the anonymous path (an unbound consent handoff, a guest
 * checkout, visitor-scoped attribution, a 401 that drops a site induction).
 */

/** Machine code on a 401 whose cause is a stale (not absent) Clerk session. */
export const CLERK_SESSION_STALE_CODE = "session_stale";

/**
 * The browser's answer to `session_stale` when clerk-js has loaded and
 * confirmed there is NO session (the session ended or was revoked, but the
 * long-lived `__client_uat` / `__session` cookies were left behind). The
 * replayed request carries it — as this header on a fetch, as this form field
 * on the setup-service server action — and the server then takes its normal
 * anonymous path. In production clerk-js does not rewrite `__client_uat` (the
 * Frontend API owns it), so without this the replay could read as stale again.
 *
 * It is a hint, not a credential: it can only make a request be treated as
 * signed out — the behaviour every route had before the stale-session signal —
 * never grant anything, and a cross-site page cannot attach a custom header to
 * a same-origin request.
 */
export const CLERK_SIGNED_OUT_HINT_HEADER = "x-tsb-clerk-signed-out";
export const CLERK_SIGNED_OUT_FORM_FIELD = "clerkSignedOut";

// `x-clerk-auth-reason` values (@clerk/backend 2.33 AuthErrorReason) that mean
// "this browser holds a signed-in Clerk client, but the request's session
// token is missing, expired, or older than the client". Anything else —
// notably `session-token-and-uat-missing` — is an anonymous visitor.
const STALE_REASON_PATTERNS = [
  /^session-token-expired/,
  /^client-uat-but-no-session-token$/,
  /^session-token-iat-before-client-uat$/,
] as const;

export function isStaleClerkAuthReason(
  reason: string | null | undefined,
): boolean {
  if (!reason) return false;
  return STALE_REASON_PATTERNS.some((pattern) => pattern.test(reason));
}

// Clerk writes the client-updated-at cookie both unsuffixed (`__client_uat`)
// and suffixed per instance (`__client_uat_<suffix>`, verified on the local
// dev instance as `__client_uat_mpjwe9X5`). The value is the client's last
// update in Unix seconds, and "0" once signed out.
const CLIENT_UAT_COOKIE = /^__client_uat(?:_[A-Za-z0-9_-]+)?$/;

/**
 * True when a cookie header (or `document.cookie`) shows a signed-in Clerk
 * client. It is a hint for loading Clerk, never an authorisation decision:
 * every server route still verifies the session token itself.
 */
export function hasSignedInClerkClient(
  cookieHeader: string | null | undefined,
): boolean {
  if (!cookieHeader) return false;
  for (const part of cookieHeader.split(";")) {
    const separator = part.indexOf("=");
    if (separator < 0) continue;
    const name = part.slice(0, separator).trim();
    if (!CLIENT_UAT_COOKIE.test(name)) continue;
    const value = Number.parseInt(part.slice(separator + 1).trim(), 10);
    if (Number.isFinite(value) && value > 0) return true;
  }
  return false;
}
